import React, { useCallback, useEffect, useState } from 'react';
import LayersHeading from 'components/customComponents/Layers/LayersHeading';
import AdvanceOptions from './AdvanceOptions';
import {
  LabelCheckbox,
  RadioGroups,
} from 'components/customComponents/Layers/InputGroups';
import { LayerInfoParentProps, SelectNameValue } from 'business/models/CustomModels';
import {
  checkIsValidInput,
  isNullOrUndefined,
  LayerType,
  setRoundedValue,
  setConcentrationLayersVolume,
  GetScoreDefn,
} from 'utils/Global';
import {
  CustomLayerDTO,
  GridLayerDTO,
  ISOPrevExpInfo,
  LayerDTO,
  LayerStateDTO,
  LayerTypeDTO,
  RandomLayerDTO,
  ScoreDefinitionDTO,
} from 'business/models/Models';
import RandomLayerExcludeIngredient from 'components/customComponents/Layers/RandomLayerExcludeIngredient';
import { usePreserveExperiment, useRMDispatch, useRMSelector } from 'redux/hooks';
import './LayerView.scss';
import { EISelectBox } from 'components/customComponents/Layers/EICheckBox';
import { SetLinkedLayers } from 'redux/reducers/layerReducer';
import { getLinkedLayerGridDto } from 'business/plateviewer/GenerateUtil';
import GridLinkedLayer from '../GridLayer/GridLinkedLayer';
import {
  selectIsEditable,
  selectIsModifyAccessRights,
} from 'redux/selectors/StateSelectors';
import { dirtifyOverLappingLayers } from 'business/plateviewer/layer/ResizeLayerTools';
import { RMModal } from 'components/modal';
import { ISO_SCORE_GROUP_NAME } from 'components/plateviewer/experiment/Optimization/OptimizationWizard';
import useModel from 'business/models/useModel';
import PlateService from 'business/services/PlateService';
import { IStyle } from 'Styles';
import RMCustomButton from 'components/customComponents/CommonComponents/RMCustomButton/RMCustomButton';
import { RMSvgIconProps } from 'components/customComponents/CommonComponents/RMSvgIcon';
import {
  updateExperimentLayers,
  updateLayerName,
} from 'redux/reducers/experimentDataReducer';
import {
  selectExpContainerDrops,
  selectExperimentLayers,
  selectSelectedLayerId,
} from 'redux/selectors/ExperimentStateSelector';
import { DrawingLayer } from 'business/models/DrawingLayer';
import { cloneDeep, debounce } from 'lodash';
import { getLayerVolume } from 'utils/ExperimentUtils';
import ImportDoe from '../ImportDoe/ImportDoe';
import { useIsScreenInUse } from 'redux/hooks/common';
import { useAllowLayerEditing } from 'redux/hooks/experiment';

type ISOScoreValue = {
  name: string;
  value: number;
};
const addIconProps: RMSvgIconProps = {
  name: 'Add',
  fill: 'white',
};

type ModalStateType = {
  showModal: boolean;
  body: any;
  heading: string;
  onOk?: () => void;
  noButtonText?: string;
  dialogClassName?: string;
  isCancelOnly: boolean;
};

export default function LayerInfoParent({
  setEditExcludeGroupId,
  setContentView,
  containerMaxWellVolume,
  isoRound = 0,
  isoPlate,
  isoScores,
}: LayerInfoParentProps) {
  const dispatch = useRMDispatch();
  const experimentLayers = useRMSelector(selectExperimentLayers);
  const _selectedLayerId = useRMSelector(selectSelectedLayerId);
  const _selectedLayer = experimentLayers.find((l) => l.id === _selectedLayerId);
  const expDrops = useRMSelector(selectExpContainerDrops);
  const setLayers = useCallback(
    (layers: LayerDTO[]) => dispatch(updateExperimentLayers(layers)),
    [dispatch],
  );
  const callPreserveUndoableState = usePreserveExperiment();
  const [showVolModal, setShowVolModal] = useState(false);
  const [layerName, setLayerName] = useState(_selectedLayer.name);
  const [layerVolume, setLayerVolume] = useState(_selectedLayer.volume);
  const [layerVolumeError, setLayerVolumeError] = useState(false);
  const [layerTarget, setLayerTarget] = useState(_selectedLayer.targetDrop);
  const [volumeBaseAddition, setVolumeBaseAddition] = useState(
    _selectedLayer.volumeBasedAddition,
  );
  const [lockedLayer, setLockedLayer] = useState(_selectedLayer.locked);
  const [overrideLayer, setOverrideLayer] = useState(_selectedLayer.overrideLayer);
  const [isoScoresData, setIsoScoresData] = useState<ISOScoreValue[]>([]);
  const [isoPreviousExp, setISOPreviousExp] = useState<ISOPrevExpInfo>(null);
  const [getPlateExpIdService] = useModel(PlateService, 'getPlateExperimentId');
  const isEditMode: boolean = useRMSelector(selectIsEditable);
  const modifyAccessRights: boolean = useRMSelector(selectIsModifyAccessRights);
  const isScreenInUse: boolean = useIsScreenInUse();
  const isLayerEditingAllowed = useAllowLayerEditing();
  const [modalStates, setModalStates] = useState<ModalStateType>({
    showModal: false,
    body: <div>Please Wait</div>,
    heading: 'ROCK MAKER',
    onOk: null,
    noButtonText: 'Cancel',
    dialogClassName: null,
    isCancelOnly: true,
  });

  useEffect(() => {
    if (
      isNullOrUndefined(isoScores) ||
      isoScores.length == 0 ||
      isoScoresData.length > 0 ||
      isNullOrUndefined(GetScoreDefn())
    ) {
      return;
    }
    const scoreDefinitions: ScoreDefinitionDTO[] = GetScoreDefn().filter(
      (sd) => sd.scoreGroupName == ISO_SCORE_GROUP_NAME,
    );
    const tempScores: ISOScoreValue[] = [];
    for (let i = 0; i < isoScores.length; i++) {
      const scorename = scoreDefinitions.find(
        (sd) => sd.wellDropScoreId === isoScores[i]?.wellDropScoreId,
      )?.scoreName;
      tempScores.push({
        name: scorename,
        value: isoScores[i].isovalue,
      } as ISOScoreValue);
    }
    setIsoScoresData([...tempScores]);
    loadPreviousExperimentData();
  });

  // In Case of Random
  const [gridSteps, setGridSteps] = useState(getGridSteps());
  let layerVaryAlong: SelectNameValue[] = [
    { name: 'Well', value: -1 },
    { name: 'Drop 1', value: 1 },
    { name: 'Drop 2', value: 2 },
  ];
  function getGridSteps() {
    if (
      isNullOrUndefined(_selectedLayer) ||
      isNullOrUndefined(_selectedLayer.layerDetail)
    ) {
      return null;
    }
    const randomLayer = _selectedLayer.layerDetail as RandomLayerDTO;
    return randomLayer.gridSteps;
  }

  function onConcentrationLayer(vol: number) {
    callPreserveUndoableState();
    const layers = cloneDeep(experimentLayers);
    const lyrVol = parseFloat(vol.toString());
    setConcentrationLayersVolume(layers, lyrVol);
    setLayers(layers);
    setShowVolModal(false);
  }
  function changeOverrideLayer() {
    callPreserveUndoableState();
    setOverrideLayer(!overrideLayer);
    let layers = cloneDeep(experimentLayers);
    let selectedLayer = layers.find((lyr) => lyr.id === _selectedLayerId);
    selectedLayer.overrideLayer = !overrideLayer;
    if (selectedLayer.overrideLayer) {
      selectedLayer.volumeBasedAddition = true;
      setVolumeBaseAddition(true);
    }
    selectedLayer.dirty = true;
    selectedLayer.layerState = LayerStateDTO.Modified;
    layers = layers.map((lyr) =>
      dirtifyOverLappingLayers(lyr, selectedLayer as DrawingLayer),
    );
    if (selectedLayer) {
      selectedLayer = { ...selectedLayer } as LayerDTO;
    }
    setLayers(layers);
  }

  function changeLockedLayer() {
    callPreserveUndoableState();
    const layers = cloneDeep(experimentLayers);
    const selectedLayer = layers.find((lyr) => lyr.id === _selectedLayerId);
    selectedLayer.locked = !lockedLayer;
    selectedLayer.layerState = LayerStateDTO.Modified;
    setLockedLayer(!lockedLayer);
    selectedLayer.dirty = true;
    setLayers(layers);
  }

  function changeVolumeBasedAddition() {
    callPreserveUndoableState();
    setVolumeBaseAddition(!volumeBaseAddition);
    let layers = cloneDeep(experimentLayers);
    let selectedLayer = layers.find((lyr) => lyr.id === _selectedLayerId);
    selectedLayer.volumeBasedAddition = !volumeBaseAddition;
    selectedLayer.dirty = true;
    selectedLayer.layerState = LayerStateDTO.Modified;
    layers = layers.map((lyr) =>
      dirtifyOverLappingLayers(lyr, selectedLayer as DrawingLayer),
    );
    if (selectedLayer) {
      selectedLayer = { ...selectedLayer } as LayerDTO;
    }
    setLayers(layers);
  }
  const updateLayerNameInRedux = debounce((layerName) => {
    dispatch(updateLayerName({ id: _selectedLayerId, name: layerName }));
  }, 200);
  const handleLayerName = (e) => {
    const layerName = e.target.value;
    setLayerName(layerName);
    updateLayerNameInRedux(layerName);
  };

  function onVolumeKeyPress(e) {
    if (e.key === 'Enter') {
      e.target.blur();
      onVolumeBlur();
    }
  }
  function handleLayerVolume(e) {
    let lyrVolume = e.target.value;
    const isValid = checkIsValidInput(lyrVolume);
    if (!isValid) {
      return;
    } else if (lyrVolume == '.') {
      lyrVolume = '0.';
    }
    if (lyrVolume === '' || parseFloat(lyrVolume.toString()) > containerMaxWellVolume) {
      setLayerVolumeError(true);
    } else {
      setLayerVolumeError(false);
    }
    setRoundedValue(lyrVolume, setLayerVolume);
  }

  function onVolumeBlur() {
    if (layerVolumeError) {
      return;
    }
    const layers = cloneDeep(experimentLayers);
    const selectedLayer = layers.find((lyr) => lyr.id === _selectedLayerId);
    selectedLayer.volume = parseFloat(layerVolume.toString());
    const lyrVolume = parseFloat(layerVolume.toString());
    if (selectedLayer.volumeBasedAddition) {
      switch (selectedLayer.type) {
        case LayerTypeDTO.Grid:
          getLinkedLayerGridDto(layers, selectedLayer.layerDetail).startWellVolume =
            lyrVolume;
          break;
        case LayerTypeDTO.Screen:
        case LayerTypeDTO.Protein:
          selectedLayer.volume = lyrVolume;
          break;
        case LayerTypeDTO.Random:
        case LayerTypeDTO.ConditionList:
          const customLayer = selectedLayer.layerDetail as CustomLayerDTO;
          customLayer.wellVolume = lyrVolume;
          break;
        default:
          break;
      }
      selectedLayer.layerState = LayerStateDTO.Modified;
      selectedLayer.dirty = true;
      callPreserveUndoableState();
      setLayers(layers);
    } else if (layerVolume !== getLayerVolume(_selectedLayer, experimentLayers)) {
      setShowVolModal(true);
    }
  }

  function getAdvanceOptions() {
    let view;
    switch (_selectedLayer.type) {
      case LayerTypeDTO.Grid:
      case LayerTypeDTO.ConditionList:
      case LayerTypeDTO.Random:
        view = (
          <div
            id="commandpanel-layers-info-advanceoptions-container"
            className="flex flex-column flex-x-center w-100pr"
            style={_styles.advanceOptionsContainer}
          >
            <fieldset
              id="commandpanel-layers-info-advanceoptions-editing"
              disabled={!isLayerEditingAllowed}
            >
              <div
                id="commandpanel-layers-info-advanceoptions-row"
                className="d-flex flex-row w-100pr align-items-start"
                style={{ gap: '2rem', marginTop: '3%' }}
              >
                <div
                  id="commandpanel-layers-info-advanceoptions-layeroptions-wrapper"
                  className="flex flex-column"
                  style={{ width: '50%' }}
                >
                  <div
                    id="commandpanel-layers-info-advanceoptions-layeroptions-title"
                    style={{
                      fontSize: '0.8125rem',
                      fontWeight: 'bold',
                      whiteSpace: 'nowrap',
                      marginBottom: '0.5rem',
                    }}
                  >
                    Layer Options
                  </div>
                  <div
                    id="commandpanel-layers-info-advanceoptions-layeroptions-group"
                    className="flex flex-column"
                    style={{ gap: '0.5rem', alignItems: 'flex-start' }}
                  >
                    <LabelCheckbox
                      labelText="Override"
                      checkBoxValue={overrideLayer}
                      onChange={changeOverrideLayer}
                      style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                      disabled={!isEditMode || !modifyAccessRights || isScreenInUse}
                    />
                    <LabelCheckbox
                      labelText="Locked"
                      checkBoxValue={lockedLayer}
                      onChange={changeLockedLayer}
                      style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                      disabled={!isEditMode || !modifyAccessRights || isScreenInUse}
                    />
                  </div>
                </div>

                <div
                  id="commandpanel-layers-info-advanceoptions-concentration-wrapper"
                  className="flex flex-column"
                  style={{ width: '50%' }}
                >
                  <div
                    id="commandpanel-layers-info-advanceoptions-concentration-title"
                    style={{
                      fontSize: '0.8125rem',
                      fontWeight: 'bold',
                      whiteSpace: 'nowrap',
                      marginBottom: '1.1rem',
                    }}
                  >
                    Concentration
                  </div>
                  <div
                    id="commandpanel-layers-info-advanceoptions-concentration-group"
                    className="flex flex-column"
                    style={{ gap: '0.5rem', alignItems: 'flex-start' }}
                  >
                    <RadioGroups
                      orientation="Vertical"
                      label1="Volume Base"
                      label2="Concentration Base"
                      radioValue={volumeBaseAddition}
                      onChange={changeVolumeBasedAddition}
                      style={{ display: 'flex', gap: '2.2rem' }}
                      disabledFirst={_selectedLayer.overrideLayer}
                      disabledSecond={_selectedLayer.overrideLayer}
                      disabled={
                        !isEditMode ||
                        !modifyAccessRights ||
                        _selectedLayer?.locked ||
                        isScreenInUse
                      }
                    />
                  </div>
                </div>
              </div>
            </fieldset>
          </div>
        );
        break;
      case LayerTypeDTO.Protein:
      case LayerTypeDTO.Screen:
      case LayerTypeDTO.Fragment:
      case LayerTypeDTO.Seed:
        view = (
          <AdvanceOptions headingText="Layer Options">
            <LabelCheckbox
              labelText={'Locked'}
              checkBoxValue={lockedLayer}
              onChange={changeLockedLayer}
              style={{ marginLeft: '2.5rem' }}
              disabled={!isEditMode || !modifyAccessRights || !isLayerEditingAllowed}
            />
          </AdvanceOptions>
        );
        break;
      default:
        view = null;

        break;
    }
    return view;
  }
  function getExtraLayersView() {
    switch (_selectedLayer.type) {
      case LayerTypeDTO.Grid:
        return (
          <>
            <div
              id="commandpanel-layers-info-extralayersview-container"
              className="flex-y-center w-100pr align-items-center"
              style={{ ..._styles.rowTopMargin, ..._styles.parentContainer }}
            >
              <div
                id="commandpanel-layers-info-extralayersview-linkedlayer-title"
                style={{ ..._styles.firstColumn, ..._styles.headingText }}
              >
                Linked Layer
              </div>
              <div
                id="commandpanel-layers-info-extralayersview-linkedlayer-actions"
                style={_styles.secondColumn}
              >
                {isEditMode && modifyAccessRights && (
                  <RMCustomButton
                    iconProps={addIconProps}
                    title={'Add'}
                    onClick={addLinkedLayer}
                    contentContainerClass={'add-linked-layer-button'}
                    isDisabled={
                      _selectedLayer?.locked || isScreenInUse || !isLayerEditingAllowed
                    }
                  ></RMCustomButton>
                )}
              </div>
            </div>
            {isLinkedLayer() && <GridLinkedLayer />}
          </>
        );

      case LayerTypeDTO.ConditionList:
      case LayerTypeDTO.Screen:
        return (
          <>
            <div
              id="commandpanel-layers-info-extralayersview-conditionlist-header"
              className="flex flex-space-between"
            >
              <LayersHeading text="Iterative Screen Info" />
              <LayersHeading text={`Round:${isoRound ? isoRound : 0}`} />
            </div>
            <div
              id="commandpanel-layers-info-extralayersview-conditionlist-details-wrapper"
              className="flex ml-40"
            >
              <div
                id="commandpanel-layers-info-extralayersview-conditionlist-details"
                className="flex flex-column"
                style={{ marginLeft: '30px', fontSize: '0.8125rem' }}
              >
                <div
                  id="commandpanel-layers-info-extralayersview-conditionlist-optimizedfrom"
                  className="flex"
                >
                  <label>Optimized from:&nbsp; </label>
                  {isoPreviousExp && (
                    <a
                      id="commandpanel-layers-info-extralayersview-conditionlist-optimizedfrom-link"
                      className="ml-2"
                      href={`/exp/${isoPreviousExp.expId}`}
                      target={`/exp/${isoPreviousExp.expId}`}
                    >
                      {isoPreviousExp.expName}
                    </a>
                  )}
                </div>
                <label id="commandpanel-layers-info-extralayersview-conditionlist-iterativestepsize-label">
                  Iterative Step Size:
                </label>
              </div>
            </div>
            <div
              id="commandpanel-layers-info-extralayersview-conditionlist-scoretable-wrapper"
              className="ml-40 mr-40"
              style={{ height: '150px', overflow: 'auto' }}
            >
              <ISOScoreTable scores={isoScoresData} />
            </div>
          </>
        );
      case LayerTypeDTO.Random:
        return (
          <RandomLayerExcludeIngredient
            setContentView={setContentView}
            setEditExcludeGroupId={setEditExcludeGroupId}
          />
        );

      default:
        return null;
    }
  }

  function getImportDoeView() {
    switch (_selectedLayer.type) {
      case LayerTypeDTO.Grid:
        return <ImportDoe />;

      default:
        return null;
    }
  }

  const handleGridStepsVlolumeChange = (e) => {
    setGridSteps(e.target.value);
  };

  const handleGridStepsVolumeKeyUp = (e) => {
    if (e.key == 'Enter') {
      if (gridSteps < 1) {
        setGridSteps(getGridSteps());
        return;
      }
      handleGridStepsVolume(gridSteps);
    }
  };

  function handleGridStepsVolume(gridStepsValue) {
    callPreserveUndoableState();
    const layers = cloneDeep(experimentLayers);
    const selectedLayer = layers.find((lyr) => lyr.id === _selectedLayerId);
    const gridSteps = gridStepsValue;
    const isValid = checkIsValidInput(gridSteps);
    const randomLayer = selectedLayer.layerDetail as RandomLayerDTO;
    if (gridSteps === '' || parseFloat(gridSteps) < 1 || isNaN(gridSteps)) {
      setRoundedValue('', setGridSteps);
    } else if (isValid || parseFloat(gridSteps) > 0) {
      randomLayer.gridSteps = parseFloat(gridSteps);
      setRoundedValue(parseInt(gridSteps).toString(), setGridSteps);
    }
    selectedLayer.dirty = true;
    setLayers(layers);
  }

  function changeLayerTarget(e) {
    callPreserveUndoableState();
    let layers = cloneDeep(experimentLayers);
    const selectedLayer = layers.find((lyr) => lyr.id === _selectedLayerId);
    const lyrTarget = Number(e.target.value);
    setLayerTarget(lyrTarget);
    selectedLayer.targetDrop = lyrTarget;
    selectedLayer.dirty = true;
    layers = layers.map((lyr) =>
      dirtifyOverLappingLayers(lyr, selectedLayer as DrawingLayer),
    );
    setLayers(layers);
  }

  function setLayerVolumByType(layerData) {
    switch (layerData.type) {
      case LayerTypeDTO.Grid:
        const grid = getLinkedLayerGridDto(experimentLayers, layerData.layerDetail);
        if (!isNullOrUndefined(grid)) {
          setLayerVolume(grid.startWellVolume);
        }

        break;
      case LayerTypeDTO.Screen:
      case LayerTypeDTO.Protein:
        setLayerVolume(layerData.volume);
        break;
      case LayerTypeDTO.Random:
      case LayerTypeDTO.ConditionList:
        setLayerVolume(layerData.layerDetail.wellVolume);
        break;
      default:
        break;
    }
  }
  function setLayerInfoValues() {
    const layers = [...experimentLayers];
    const selectedLayer = layers.find((lyr) => lyr.id === _selectedLayerId);
    if (isNullOrUndefined(selectedLayer)) {
      return;
    }
    setLayerName(selectedLayer.name);
    setLayerVolumByType(selectedLayer);
    setLayerTarget(selectedLayer.targetDrop);
    setVolumeBaseAddition(
      isNullOrUndefined(selectedLayer.volumeBasedAddition)
        ? false
        : selectedLayer.volumeBasedAddition,
    );
    setLockedLayer(selectedLayer.locked);
    setOverrideLayer(selectedLayer.overrideLayer);
    if (selectedLayer.type === LayerTypeDTO.Random) {
      setGridSteps((selectedLayer.layerDetail as RandomLayerDTO).gridSteps);
    }
  }

  useEffect(() => {
    setLayerInfoValues();
  }, [_selectedLayerId]);

  const _setLinkedLayers = useCallback(
    (layers: LayerDTO) => dispatch(SetLinkedLayers(layers)),
    [dispatch],
  );

  function addLinkedLayer() {
    callPreserveUndoableState();
    _setLinkedLayers({ ..._selectedLayer } as LayerDTO);
  }
  function getTargetList() {
    if (window.location.pathname.includes('/screen')) {
      layerVaryAlong = [{ name: 'Well', value: -1 }];
    } else if (
      _selectedLayer.type === LayerType.Seed ||
      _selectedLayer.type === LayerType.Protein ||
      _selectedLayer.type === LayerType.Fragment
    ) {
      layerVaryAlong = [];
      layerVaryAlong.push(...getExpDrops());
    } else {
      layerVaryAlong = [];
      layerVaryAlong.push({ name: 'Well', value: -1 });
      layerVaryAlong.push(...getExpDrops());
    }
    return layerVaryAlong;
  }

  const getExpDrops = (): SelectNameValue[] => {
    const dropsArr: SelectNameValue[] = [];
    expDrops?.forEach((element) => {
      dropsArr.push({ name: 'Drop ' + element.dropNumber, value: element.dropNumber });
    });
    return dropsArr;
  };

  function isLinkedLayer(): boolean {
    const layers = [...experimentLayers];
    const selectedLayer = layers.find((lyr) => lyr.id === _selectedLayerId) as LayerDTO;
    const gridLayer = selectedLayer.layerDetail as GridLayerDTO;
    if (
      _selectedLayer.type == LayerTypeDTO.Grid &&
      experimentLayers.findIndex(
        (lyr) =>
          lyr.id != _selectedLayerId &&
          lyr.type == LayerTypeDTO.Grid &&
          (lyr.layerDetail as GridLayerDTO).gridId == gridLayer.gridId,
      ) > -1
    ) {
      return true;
    }
    return false;
  }

  async function loadPreviousExperimentData() {
    const prevExpInfo = await getPlateExpIdService(isoPlate);
    setISOPreviousExp(prevExpInfo);
  }

  const setShowModalFalse = () => {
    setModalStates({ ...modalStates, showModal: false });
  };

  return (
    <>
      <RMModal
        id="commandpanel-layers-layerinfoparent-changeconcentration"
        showModal={showVolModal}
        setShowModal={setShowVolModal}
        onOk={() => onConcentrationLayer(layerVolume)}
        body="This change will affect all concentration-based layers. Continue?"
        heading="Change Concentration"
      />
      <RMModal
        id="commandpanel-layers-layerinfoparent-genericmodal"
        {...modalStates}
        setShowModal={setShowModalFalse}
      />

      <fieldset
        id="commandpanel-layers-layerinfoparent-fieldset"
        disabled={!isLayerEditingAllowed}
      >
        <div
          id="commandpanel-layers-layerinfoparent-container"
          className="flex flex-column flex-x-center"
        >
          <div
            id="commandpanel-layers-layerinfoparent-layername-row"
            className="d-flex flex-row w-100pr align-items-center"
          >
            <div
              id="commandpanel-layers-layerinfoparent-layername-title"
              style={{ ..._styles.firstColumn, ..._styles.titleText }}
            >
              Layer Name
            </div>
            <div
              id="commandpanel-layers-layerinfoparent-layername-inputcontainer"
              style={_styles.secondColumn}
            >
              <input
                id="commandpanel-layers-layerinfoparent-layername-input"
                style={{
                  width: '100%',
                  height: '2.125rem',
                  paddingLeft: '9px',
                }}
                value={layerName}
                onChange={handleLayerName}
                type="text"
                disabled={
                  !isEditMode ||
                  !modifyAccessRights ||
                  _selectedLayer?.locked ||
                  isScreenInUse
                }
              />
            </div>
          </div>
          <div
            id="commandpanel-layers-layerinfoparent-target-row"
            className="d-flex flex-row w-100pr align-items-center"
            style={_styles.rowTopMargin}
          >
            <div
              id="commandpanel-layers-layerinfoparent-target-title"
              style={{ ..._styles.firstColumn, ..._styles.titleText }}
            >
              Target
            </div>
            <div
              id="commandpanel-layers-layerinfoparent-target-container"
              style={{ ..._styles.secondColumn, ..._styles.secondRow }}
            >
              <EISelectBox
                selectedValue={layerTarget}
                onChange={changeLayerTarget}
                options={getTargetList()}
                selectStyle={{ height: '100%', width: '100%' }}
                containerStyle={{
                  width: '35%',
                  display: 'flex',
                  justifyContent: 'flex-start',
                  height: '100%',
                }}
                disabled={
                  _selectedLayer?.locked ||
                  !isEditMode ||
                  !modifyAccessRights ||
                  isScreenInUse
                }
              />
              {_selectedLayer.type !== LayerTypeDTO.Protein &&
                _selectedLayer.type !== LayerTypeDTO.Seed &&
                _selectedLayer.type !== LayerTypeDTO.Fragment && (
                  <>
                    <div
                      id="commandpanel-layers-layerinfoparent-volume-title"
                      style={_styles.titleText}
                    >
                      Volume
                    </div>

                    <div
                      id="commandpanel-layers-layerinfoparent-volume-inputwrapper"
                      className="d-flex flex-row align-items-center"
                      style={_styles.inputWithPostFix}
                    >
                      <input
                        id="commandpanel-layers-layerinfoparent-volume-input"
                        style={_styles.volumeInput}
                        className={layerVolumeError ? 'input-error' : ''}
                        onBlur={onVolumeBlur}
                        onKeyPress={onVolumeKeyPress}
                        value={layerVolume}
                        onChange={handleLayerVolume}
                        type="text"
                        disabled={
                          !isEditMode ||
                          !modifyAccessRights ||
                          _selectedLayer?.locked ||
                          isScreenInUse
                        }
                      />
                    </div>
                    <div
                      id="commandpanel-layers-layerinfoparent-volume-postfix"
                      style={_styles.postFixContainer}
                    >
                      uL
                    </div>
                  </>
                )}
            </div>
          </div>
          {_selectedLayer.type === LayerTypeDTO.Random && (
            <div
              id="commandpanel-layers-layerinfoparent-gridsteps-wrapper"
              className="flex w-100pr"
            >
              {isNullOrUndefined(gridSteps) ? null : (
                <div
                  id="commandpanel-layers-layerinfoparent-gridsteps-row"
                  className="d-flex flex-row w-100pr align-items-center"
                  style={_styles.rowTopMargin}
                >
                  <div
                    id="commandpanel-layers-layerinfoparent-gridsteps-title"
                    style={{ ..._styles.firstColumn, ..._styles.titleText }}
                  >
                    Grid Steps
                  </div>
                  <div
                    id="commandpanel-layers-layerinfoparent-gridsteps-inputcontainer"
                    style={{ ..._styles.secondColumn, ..._styles.secondRow }}
                  >
                    <input
                      id="commandpanel-layers-layerinfoparent-gridsteps-input"
                      style={_styles.gridStepsInput}
                      value={gridSteps}
                      onChange={handleGridStepsVlolumeChange}
                      onKeyUp={handleGridStepsVolumeKeyUp}
                      type="text"
                      disabled={
                        !isEditMode ||
                        !modifyAccessRights ||
                        _selectedLayer?.locked ||
                        isScreenInUse
                      }
                    />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </fieldset>
      <div
        id="commandpanel-layers-layerinfoparent-advancedoptions-title"
        style={{ ..._styles.headingText, ..._styles.rowTopMargin }}
      >
        Advanced Options
      </div>
      <div
        id="commandpanel-layers-layerinfoparent-advancedoptions-container"
        className="flex w-100pr"
      >
        {getAdvanceOptions()}
      </div>
      {getImportDoeView()}
      {getExtraLayersView()}
    </>
  );
}

type ISOSCoreTableProps = {
  scores: ISOScoreValue[];
};
const ISOScoreTable = ({ scores }: ISOSCoreTableProps) => {
  return (
    <table
      id="commandpanel-layerinfoparent-isoscoretable-table"
      className="optimization-table"
      style={{ overflow: 'auto', width: '100%' }}
    >
      <thead
        id="commandpanel-layerinfoparent-isoscoretable-thead"
        style={{ position: 'sticky', top: '0' }}
      >
        <tr
          id="commandpanel-layerinfoparent-isoscoretable-header-row"
          style={{ display: 'flex' }}
        >
          <th
            id="commandpanel-layerinfoparent-isoscoretable-header-scorename"
            style={{ flex: '60%' }}
          >
            Score Name
          </th>
          <th
            id="commandpanel-layerinfoparent-isoscoretable-header-isovalue"
            style={{ flex: '40%' }}
          >
            ISO Value
          </th>
        </tr>
      </thead>
      <tbody id="commandpanel-layerinfoparent-isoscoretable-tbody">
        {scores.map((s, i) => {
          return (
            <tr
              id="commandpanel-layerinfoparent-isoscoretable-container"
              key={`isoScr-${s.value + i}`}
              style={{ display: 'flex' }}
            >
              <td
                id={`commandpanel-layerinfoparent-isoscoretable-name-${s.name}`}
                style={{ flex: '60%' }}
              >
                {s.name}
              </td>
              <td
                id={`commandpanel-layerinfoparent-isoscoretable-name-${s.value}`}
                style={{ flex: '40%' }}
              >
                {s.value}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

const _styles: IStyle = {
  parentContainer: {
    height: '2.125rem',
  },
  firstColumn: {
    width: '25%',
  },
  secondColumn: {
    width: '75%',
  },
  thirdColumn: {
    width: '62%',
  },
  rowTopMargin: {
    marginTop: '2%',
  },
  titleText: {
    fontSize: '0.8125rem',
    fontWeight: 400,
    alignItems: 'center',
    justifyContent: 'flex-start',
    whiteSpace: 'nowrap',
  },
  secondRow: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    height: '2.125rem',
  },
  volumeInput: {
    width: '100%',
    height: '2.125rem',
    border: 'none',
    paddingLeft: '9px',
  },
  inputWithPostFix: {
    border: 'solid 1px',
    width: '40%',
  },
  postFixContainer: {
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  gridStepsInput: {
    width: '20%',
  },
  headingText: {
    fontSize: '0.8125rem',
    fontWeight: 'bold',
    alignItems: 'center',
    justifyContent: 'flex-start',
    whiteSpace: 'nowrap',
  },
  advanceOptionsContainer: {
    marginLeft: '1%',
  },
};
